import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../api/axios';
import { Container, Box, TextField, Button, Typography, Paper, Alert, Link } from '@mui/material';
import PageAnimation from '../components/animations/PageAnimation';
import SEO from '../components/SEO';

function LoginPage() {
    // View can be 'login', 'forgot', or 'reset'
    const [view, setView] = useState('login');

    // State for all forms
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');

    // State for messages
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const navigate = useNavigate();

    // Reset all fields and messages
    const resetState = () => {
        setEmail('');
        setPassword('');
        setOtp('');
        setNewPassword('');
        setError('');
        setSuccess('');
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        try {
            const response = await api.post('/login', { email, password });

            // Save token (existing)
            localStorage.setItem('accessToken', response.data.access_token);

            // Save contactPerson so Header can read it
            if (response.data.contactPerson) {
                localStorage.setItem('contactPerson', response.data.contactPerson);
            } else {
                // fallback: use email or companyName if contactPerson isn't returned
                localStorage.setItem('contactPerson', response.data.email || response.data.companyName || '');
            }

            // Save parent group for multi-account dashboard
            const parentGroup = response.data.parent_group || 'NA';
            localStorage.setItem('parentGroup', parentGroup);
            localStorage.setItem('isParent', response.data.is_parent ? 'true' : 'false');
            // Direct login never counts as "switched from parent"
            localStorage.setItem('switchedByParent', 'false');

            // (optional) store other fields you might need
            if (response.data.companyName) localStorage.setItem('companyName', response.data.companyName);
            if (response.data.email) localStorage.setItem('email', response.data.email);

            // notify other parts of the app (you already do this)
            window.dispatchEvent(new Event('storage'));

            if (response.data.is_parent) {
                // User IS the parent contact — always redirect to parent dashboard
                localStorage.removeItem('profileIncomplete');
                navigate('/parent-dashboard');
            } else if (response.data.incomplete_profile) {
                localStorage.setItem('profileIncomplete', 'true');
                navigate('/profile');
            } else {
                localStorage.removeItem('profileIncomplete');
                navigate('/browse');
            }
        } catch (err) {
            setError(err.response?.data?.msg || 'Login failed. Please try again.');
        }
    };

    const handleRequestOtpSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        try {
            const response = await api.post('/request-password-reset', { email });
            setSuccess(response.data.msg);
            setView('reset'); // Move to the next step
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to request OTP. Please try again.');
        }
    };

    const handleResetPasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        try {
            const response = await api.post('/reset-password', { email, otp, newPassword });
            setSuccess(response.data.msg + ' You can now log in with your new password.');
            resetState();
            setView('login'); // Return to login view on success
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to reset password. Please try again.');
        }
    };

    const commonTextFieldStyles = {
        "& label.Mui-focused": { color: "#75a84b" },
        "& .MuiInput-underline:after": { borderBottomColor: "#75a84b" },
        "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#75a84b" },
            "&:hover fieldset": { borderColor: "#75a84b" },
            "&.Mui-focused fieldset": { borderColor: "#75a84b" },
        }
    };

    // --- RENDER LOGIC ---
    const renderContent = () => {
        if (view === 'forgot') {
            return (
                <>
                    <Typography component="h1" variant="h5">
                        Forgot Password
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                        Enter your email to receive a reset code.
                    </Typography>
                    <Box component="form" onSubmit={handleRequestOtpSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={commonTextFieldStyles}
                        />
                        <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 1, py: 1.5, bgcolor: "#75a84b" }}>
                            Send Reset Code
                        </Button>
                        <Box textAlign="center">
                            <Link component="button" variant="body2" onClick={() => { setView('login'); resetState(); }} sx={{ color: "#75a84b", textDecoration: "underline", cursor: "pointer" }}>
                                Back to Sign In
                            </Link>
                        </Box>
                    </Box>
                </>
            );
        }

        if (view === 'reset') {
            return (
                <>
                    <Typography component="h1" variant="h5">
                        Reset Your Password
                    </Typography>
                    <Box component="form" onSubmit={handleResetPasswordSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                        <TextField
                            margin="normal" required fullWidth disabled
                            label="Email Address" value={email} sx={commonTextFieldStyles}
                        />
                        <TextField
                            margin="normal" required fullWidth autoFocus
                            label="6-Digit Code" value={otp} onChange={(e) => setOtp(e.target.value)}
                            sx={commonTextFieldStyles}
                        />
                        <TextField
                            margin="normal" required fullWidth
                            name="newPassword" label="New Password" type="password" id="newPassword"
                            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                            sx={commonTextFieldStyles}
                        />
                        <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 1, py: 1.5, bgcolor: "#75a84b" }}>
                            Set New Password
                        </Button>
                        <Box textAlign="center">
                            <Link component="button" variant="body2" onClick={() => { setView('login'); resetState(); }} sx={{ color: "#75a84b", textDecoration: "underline", cursor: "pointer" }}>
                                Back to Sign In
                            </Link>
                        </Box>
                    </Box>
                </>
            );
        }

        // Default view: 'login'
        return (
            <>
                <Typography component="h1" variant="h5">
                    Sign In
                </Typography>
                <Box component="form" onSubmit={handleLoginSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                    <TextField
                        margin="normal" required fullWidth id="email" label="Email or Contact Person" name="email"
                        autoComplete="email" autoFocus value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={commonTextFieldStyles}
                    />
                    <TextField
                        margin="normal" required fullWidth name="password" label="Password" type="password" id="password"
                        autoComplete="current-password" value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={commonTextFieldStyles}
                    />
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1, mt: 1 }}>
                        <Link component={RouterLink} to="/register" variant="body2" sx={{ color: "#75a84b" }}>
                            No Account? Register
                        </Link>
                        {/* --- NEW: Forgot Password Link --- */}
                        <Link component="button" type="button" variant="body2" onClick={() => { setView('forgot'); resetState(); }} sx={{ color: "#75a84b", textDecoration: "underline", cursor: "pointer" }}>
                            Forgot Password?
                        </Link>
                    </Box>
                    <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 2, py: 1.5, bgcolor: "#75a84b" }}>
                        Sign In
                    </Button>
                </Box>
            </>
        );
    };

    return (
        <PageAnimation>
            <SEO
                title="Sign In | Matax Express"
                description="Log in to your Matax Express wholesale account. Access your orders, cart, and business profile."
                url="https://mataxexpress.ca/login"
                noindex={true}
            />
            <Container maxWidth="xs">
                <Paper elevation={6} sx={{ mt: 8, p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 2, color: "#75a84b" }}>
                    {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success}</Alert>}
                    {renderContent()}
                </Paper>
            </Container>
        </PageAnimation>
    );
}

export default LoginPage;
